import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Autocomplete, TextField, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEventListener } from '@mui/x-data-grid';
import { useAppSelector } from '../../../store/hooks';
import { BaseDataGrid } from '../../../components';
import { useAdmissionMethodsColumns, useAdmissionMethodsQuery } from '../hooks';
import { admissionMethodApi } from '../../../services';
import { AdmissionMethodSchema } from '../../../services/admissionMethodApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type AdmissionMethod = z.infer<typeof AdmissionMethodSchema>;

const AdmissionMethodsToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm phương thức xét tuyển...',
	exportFn: (params) => admissionMethodApi.exportToExcel(params),
});

interface AdmissionMethodsByMajorTabProps {
	onViewMethod: (method: AdmissionMethod, maNganh?: string) => void;
}

export function AdmissionMethodsByMajorTab({ onViewMethod }: AdmissionMethodsByMajorTabProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const majors = useAppSelector((state) => state.filters.majors);
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	// Filter majors by selected faculty (zero-trust: backend also enforces this)
	const filteredMajors = useMemo(() => {
		if (!selectedFaculty) return majors;
		return majors.filter((m) => m.maKhoa === selectedFaculty);
	}, [majors, selectedFaculty]);

	// Major filter state
	const [selectedMajor, setSelectedMajor] = useState<{ value: string; label: string } | null>(null);

	// Reset selected major when faculty changes (selected major may no longer be valid)
	const prevFacultyRef = useRef(selectedFaculty);
	useEffect(() => {
		if (prevFacultyRef.current !== selectedFaculty) {
			prevFacultyRef.current = selectedFaculty;
			setSelectedMajor(null);
		}
	}, [selectedFaculty]);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch, resetPage,
	} = useEntityListState({ defaultSortField: 'tenPhuongThuc' });

	// Query with faculty + major filters (faculty filter auto-applied from global state)
	const queryParams: StatsQueryParams = {
		filters: {
			query: searchValue,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined,
			maNganh: selectedMajor ? [selectedMajor.value] : undefined,
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as
			| 'asc'
			| 'desc'
			| undefined,
	};
	const { data: admissionMethodsData, isLoading, refetch } = useAdmissionMethodsQuery(queryParams);
	const exportParams = useAdmissionMethodsQuery.buildExportParams(queryParams);

	const admissionMethods = admissionMethodsData?.data?.stats || [];
	const totalCount = admissionMethodsData?.pagination?.total || 0;

	const handleRowClick: GridEventListener<'rowClick'> = useCallback(
		(params) => {
			onViewMethod(params.row as AdmissionMethod, selectedMajor?.value);
		},
		[onViewMethod, selectedMajor],
	);

	const handleRefresh = useCallback(() => {
		if (isLoading) return;
		refetch();
	}, [isLoading, refetch]);

	// No-op handlers for columns (read-only tab, no edit/delete)
	const handleEdit = useCallback(
		(_admissionMethod: AdmissionMethod) => () => { },
		[],
	);
	const handleDelete = useCallback(
		(_admissionMethod: AdmissionMethod) => () => { },
		[],
	);

	const columns = useAdmissionMethodsColumns({
		isAdmin: false,
		isMobile,
		admissionMethodStats: admissionMethodsData?.data,
		onEdit: handleEdit,
		onDelete: handleDelete,
	});

	const handleMajorChange = useCallback(
		(_event: React.SyntheticEvent, value: { value: string; label: string } | null) => {
			setSelectedMajor(value);
			resetPage();
		},
		[resetPage],
	);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Major filter */}
			<Box sx={{ px: isMobile ? 1 : 2, pt: 1, pb: 0.5 }}>
				<Autocomplete
					size="small"
					options={filteredMajors}
					value={selectedMajor}
					onChange={handleMajorChange}
					getOptionLabel={(option) => `${option.value} - ${option.label}`}
					isOptionEqualToValue={(option, value) => option.value === value.value}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Lọc theo ngành"
							placeholder="Chọn ngành..."
						/>
					)}
					sx={{ maxWidth: isMobile ? '100%' : 400 }}
				/>
			</Box>

			{/* Data grid */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<BaseDataGrid
					rows={admissionMethods}
					columns={columns}
					loading={isLoading}
					onRowClick={handleRowClick}
					getRowId={(row) => row.maPhuongThuc}
					rowCount={totalCount}
					paginationModel={paginationModel}
					onPaginationModelChange={handlePaginationModelChange}
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					filterModel={filterModel}
					onFilterModelChange={handleFilterModelChange}
					pageSizeOptions={[10, 25, 50, 100]}
					paginationMode="server"
					sortingMode="server"
					filterMode="server"
					disableRowSelectionOnClick
					rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
					getRowHeight={isMobile ? () => 'auto' : undefined}
					slots={{
						toolbar: () => (
							<AdmissionMethodsToolbar
								searchValue={searchValue}
								onSearch={handleSearch}
								onRefresh={handleRefresh}
								exportParams={exportParams}
								isMobile={isMobile}
							/>
						),
					}}
				/>
			</Box>
		</Box>
	);
}

export default AdmissionMethodsByMajorTab;
